// Web HID gamepad bridge. Bypasses Chromium's Gamepad API (which is
// unreliable for wired Xbox controllers on macOS) by opening the HID
// device directly from the renderer and parsing raw reports.
//
// Public surface:
//   hidGamepadBus. Shared EventTarget, same event names
//                                 as gamepadPoll: button-down /
//                                 button-up / axis.
//   requestHidGamepad(). Shows Chromium's HID picker so the
//                                 user grants access to a controller.
//                                 Resolves with the chosen device or
//                                 null. Call from a user gesture.
//   autoReconnectHidGamepads(). Opens any device the session has
//                                 previously granted without showing
//                                 the picker. Safe to call on app
//                                 mount; no-ops if Web HID is missing.
//   stopHidGamepad(device). Close a device and detach listeners.
//
// We emit events in the same shape as gamepadPoll so gamepadPoll.js
// can re-emit them to its own listeners, giving every Phase 1-3
// consumer HID support for free.

import { parseXboxReport, diffReports } from './xboxHidParser';

// Broadcast channel shared across the renderer. gamepadPoll subscribes
// to this and re-dispatches into its own listeners so downstream code
// stays oblivious to the event source.
export const hidGamepadBus = new EventTarget();
function emit(name, detail) { hidGamepadBus.dispatchEvent(new CustomEvent(name, { detail })); }

// Per-device state. Map<HIDDevice, { prev, off }>.
const openDevices = new Map();

// Rolling log of raw reports for on-screen debugging. Stores the
// most recent unique (reportId, bytes) pairs so the settings panel
// can render them in hex. Capped so we don't grow unbounded.
const DEBUG_LOG_LIMIT = 8;
const debugLog = [];
const debugListeners = new Set();
function pushDebug(entry) {
  const prev = debugLog[debugLog.length - 1];
  if (prev && prev.hex === entry.hex) return; // collapse repeats
  debugLog.push(entry);
  while (debugLog.length > DEBUG_LOG_LIMIT) debugLog.shift();
  for (const fn of debugListeners) fn(debugLog.slice());
}
export function subscribeHidDebug(fn) {
  debugListeners.add(fn);
  fn(debugLog.slice());
  return () => debugListeners.delete(fn);
}
export function clearHidDebug() {
  debugLog.length = 0;
  for (const fn of debugListeners) fn([]);
}

const XBOX_VID = 0x045E;
const VENDOR_FILTERS = [
  { vendorId: XBOX_VID },
  // Room to grow: DualSense (Sony), Switch Pro (Nintendo) parsers can
  // plug in alongside Xbox when we add their report formats. For now
  // we only filter in the picker; the parse path no-ops for anything
  // that isn't an Xbox report.
];

function isSupported() {
  return typeof navigator !== 'undefined' && 'hid' in navigator;
}

function parserFor(device) {
  if (device.vendorId === XBOX_VID) return parseXboxReport;
  return null;
}

function onInputReport(device, event) {
  // Always capture the raw bytes for the on-screen debug panel so we
  // can diagnose parser mismatches without DevTools.
  const bytes = new Uint8Array(event.data.buffer);
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join(' ');
  pushDebug({ reportId: event.reportId, length: bytes.length, hex, at: Date.now() });

  const parse = parserFor(device);
  if (!parse) return;
  const snapshot = parse(event.data);
  if (!snapshot) return;
  const state = openDevices.get(device);
  const diff = diffReports(state?.prev || null, snapshot);
  for (const change of diff.buttonChanges) {
    emit(change.pressed ? 'button-down' : 'button-up', { index: change.index });
  }
  if (diff.axisChanged) emit('axis', { axes: snapshot.axes.slice() });
  if (state) state.prev = snapshot;
}

async function openDevice(device) {
  if (openDevices.has(device)) return device;
  try {
    if (!device.opened) await device.open();
  } catch (err) {
    console.warn('[hidGamepadBridge] open failed', device.productName, err);
    return null;
  }
  const handler = (e) => onInputReport(device, e);
  device.addEventListener('inputreport', handler);
  const disconnect = () => {
    stopHidGamepad(device);
  };
  device.addEventListener('close', disconnect, { once: true });
  openDevices.set(device, { prev: null, handler });
  return device;
}

export async function requestHidGamepad() {
  if (!isSupported()) return null;
  try {
    const devices = await navigator.hid.requestDevice({ filters: VENDOR_FILTERS });
    const device = Array.isArray(devices) ? devices[0] : devices;
    if (!device) return null;
    return await openDevice(device);
  } catch (err) {
    console.warn('[hidGamepadBridge] requestDevice failed', err);
    return null;
  }
}

export async function autoReconnectHidGamepads() {
  if (!isSupported()) return [];
  try {
    const devices = await navigator.hid.getDevices();
    const supported = devices.filter((d) => parserFor(d));
    const opened = [];
    for (const d of supported) {
      const result = await openDevice(d);
      if (result) opened.push(result);
    }
    return opened;
  } catch (err) {
    console.warn('[hidGamepadBridge] autoReconnect failed', err);
    return [];
  }
}

export function stopHidGamepad(device) {
  const state = openDevices.get(device);
  if (!state) return;
  try { device.removeEventListener('inputreport', state.handler); } catch (_) {}
  try { if (device.opened) device.close(); } catch (_) {}
  openDevices.delete(device);
}

export function isHidGamepadConnected() {
  return openDevices.size > 0;
}

// Convenience: attach a subscription helper similar to gamepadPoll's
// `.on`. Returned function detaches the listener.
export function onHidEvent(name, fn) {
  const handler = (e) => fn(e);
  hidGamepadBus.addEventListener(name, handler);
  return () => hidGamepadBus.removeEventListener(name, handler);
}
